/**
 * Embroidery personalization builder.
 *
 * Placement eligibility and options are decided entirely by Liquid (from the
 * product's own tagged image alt text - see embroidery-builder.liquid and
 * embroidery-placement-label.liquid) and rendered as real Step 2 buttons and
 * preview images before this ever runs - this file never re-derives
 * placements from alt text itself, it only reacts to the already-rendered
 * buttons via their `data-media-id`. The one exception is duplicate-label
 * detection (see logDuplicatePlacements()), which only needs a console
 * warning and reads the JSON payload Liquid already computed for that.
 *
 * Scoped per <embroidery-builder> instance so multiple product forms on the
 * same page (e.g. theme-editor section reloads) never share state or double
 * up event listeners. Modal accessibility (focus trap, Escape, scroll lock,
 * returning focus to the trigger) is handled entirely by the theme's own
 * <popup-component>/<dialog> pair - this file only owns the 4-step wizard
 * logic and writes the finished selections into the real product form's
 * hidden `properties[...]` inputs via their `form="..."` attribute.
 *
 * Selections are also mirrored into sessionStorage, keyed per product, so
 * navigating away (e.g. to the cart) and back to the same product's page
 * within the same browser session restores the draft - completed or not -
 * instead of starting over. This never touches an already-added cart line;
 * it only pre-fills the builder for the *next* add-to-cart.
 */
(function () {
  'use strict';

  if (customElements.get('embroidery-builder')) return;

  var TOTAL_STEPS = 4;

  /**
   * Patches are inserted directly into the text field as a single character
   * each, so they can sit anywhere in the string (start, middle, end,
   * between letters) and count toward the max length like any other
   * character - see the "Add Patch" section rewrite in this file's history.
   * Each marker is one Unicode code point (verified via Array.from, not
   * .length, since a couple of these are UTF-16 surrogate pairs) so
   * position-based logic (char counting, per-letter color indexing) stays
   * correct as long as it iterates by code point rather than by
   * String.prototype.length/.charAt.
   */
  var PATCH_MARKERS = {
    Cloud: '☁',
    Zzz: '💤',
    Star: '⭐',
    Heart: '❤',
  };
  var PATCH_NAMES_BY_MARKER = {};
  Object.keys(PATCH_MARKERS).forEach(function (name) {
    PATCH_NAMES_BY_MARKER[PATCH_MARKERS[name]] = name;
  });

  function defaultState() {
    return {
      step: 1,
      type: null,
      placement: null, // the placement label written to properties[Embroidery Placement]
      placementMediaId: null, // the tagged image's Shopify media id - keys the preview lookup, see updatePreview()
      style: null,
      text: '',
      threadColor: null, // Custom Name: one color for the whole string
      threadColorLetters: [], // Monogram Initials: one color per position (letter or patch), index-aligned with `text`
      completed: false,
      addedToCart: false, // has a real add-to-cart submission actually been attempted for this state - see verifyStillInCart()
    };
  }

  class EmbroideryBuilder extends HTMLElement {
    constructor() {
      super();
      this.state = defaultState();
    }

    connectedCallback() {
      this.popup = this.querySelector('popup-component');
      this.dialog = this.querySelector('dialog');
      this.triggerButton = this.querySelector('[data-embroidery-trigger]');
      this.triggerText = this.querySelector('[data-embroidery-trigger-text]');
      this.summaryEl = this.querySelector('[data-embroidery-summary]');
      this.summaryTextEl = this.querySelector('[data-embroidery-summary-text]');
      this.removeButton = this.querySelector('[data-embroidery-remove]');
      this.stepIndicator = this.querySelector('[data-embroidery-step-indicator]');
      this.stepSections = Array.prototype.slice.call(this.querySelectorAll('[data-embroidery-step]'));
      this.optionButtons = Array.prototype.slice.call(this.querySelectorAll('[data-embroidery-option]'));
      this.mediaEl = this.querySelector('[data-embroidery-media]');
      this.previewImages = Array.prototype.slice.call(this.querySelectorAll('[data-embroidery-preview]'));
      this.liveTextEl = this.querySelector('[data-embroidery-live-text]');
      this.textInput = this.querySelector('[data-embroidery-text-input]');
      this.patchInsertButtons = Array.prototype.slice.call(this.querySelectorAll('[data-embroidery-patch-insert]'));
      this.patchStatusEl = this.querySelector('[data-embroidery-patch-status]');
      this.charCount = this.querySelector('[data-embroidery-char-count]');
      this.personalizeHeading = this.querySelector('[data-embroidery-personalize-heading]');
      this.textLabel = this.querySelector('[data-embroidery-text-label]');
      this.initialsColorsEl = this.querySelector('[data-embroidery-initials-colors]');
      this.nameColorsEl = this.querySelector('[data-embroidery-name-colors]');
      this.letterSwatchGroups = Array.prototype.slice.call(this.querySelectorAll('[data-embroidery-letter-swatches]'));
      this.letterLabels = Array.prototype.slice.call(this.querySelectorAll('[data-embroidery-letter-label]'));
      this.backButton = this.querySelector('[data-embroidery-back]');
      this.nextButton = this.querySelector('[data-embroidery-next]');
      this.finishButton = this.querySelector('[data-embroidery-finish]');

      this.fields = {};
      Array.prototype.forEach.call(this.querySelectorAll('[data-embroidery-field]'), function (input) {
        this.fields[input.dataset.embroideryField] = input;
      }.bind(this));

      this.addLabel = this.dataset.addLabel || (this.triggerText && this.triggerText.textContent.trim()) || '✨ Add Embroidery ✨';
      this.editLabel = this.dataset.editLabel || '✨ Edit Embroidery ✨';
      this.initialsMaxLength = parseInt(this.dataset.initialsMaxLength, 10) || 3;
      this.nameMaxLength = parseInt(this.dataset.nameMaxLength, 10) || 12;
      this.feeVariantId = (window.theme && window.theme.embroideryFeeVariantId) || '';
      this.productId = this.dataset.productId || '';
      this.productTitle = this.dataset.productTitle || '';
      this.sectionId = this.dataset.sectionId || '';
      this.productFormId = this.dataset.productForm || '';
      this.priceAddonEl = this.querySelector('[data-embroidery-price-addon]');

      this.loadPersistedState();
      this.logDuplicatePlacements();

      this.reattachPriceAddon();
      this.autoSelectSinglePlacement();
      this.bindEvents();
      if (this.state.type) this.syncPersonalizationForType();
      this.updateLetterColorGroups();
      this.renderStep();
      this.updatePreview();
      this.updateNavState();

      if (this.state.completed) {
        this.applyFieldsFromState();
        this.renderSummary();
        this.updatePriceAddon();

        // Render optimistically from the local draft first (instant, no
        // flash for the common case), then confirm in the background - the
        // draft only knows what this browser last did, not that the
        // customer went to the cart afterward and removed the embroidery
        // fee, which strips the cart line's properties server-side with no
        // way to notify this page directly.
        if (this.state.addedToCart) this.verifyStillInCart();
      }
    }

    /**
     * Confirms the persisted "completed" draft still corresponds to a real
     * embroidered line in the cart, for a draft that was actually submitted
     * at least once (see handleFormSubmit()). A draft that was only ever
     * finished in the modal but never added is left alone regardless of
     * cart contents - that's the normal pre-fill case, not a stale one.
     * If the matching cart line is gone (removed outright, or its
     * embroidery stripped by embroidery-fee-sync.js after the fee was
     * removed), resets the builder back to "Add Embroidery" so the PDP
     * never keeps advertising embroidery the cart no longer reflects.
     */
    verifyStillInCart() {
      if (!this.productId || !window.theme || !window.theme.routes) return;

      fetch(window.theme.routes.cart_url + '.js', { headers: { Accept: 'application/json' } })
        .then(function (response) {
          return response.json();
        })
        .then(
          function (cart) {
            var stillEmbroidered = cart.items.some(function (item) {
              return (
                String(item.product_id) === String(this.productId) &&
                item.properties &&
                item.properties['Embroidery'] === 'Yes'
              );
            }, this);

            if (!stillEmbroidered) this.handleRemove();
          }.bind(this)
        )
        .catch(function () {
          // Cart unreachable - leave the optimistic render as-is rather
          // than resetting a possibly-still-valid draft on a network hiccup.
        });
    }

    /**
     * @returns {string} the sessionStorage key for this product's draft, or
     *   '' if this instance has no product id to scope by (storage is
     *   skipped entirely in that case rather than risk cross-product bleed).
     */
    storageKey() {
      return this.productId ? 'embroidery-draft-' + this.productId : '';
    }

    /**
     * Restores a previously-saved draft for this product, if any. Always
     * reopens on step 1 regardless of where the customer left off, matching
     * how a fresh "Add Embroidery" click behaves - only the selections
     * themselves (and whether they'd finished) carry over.
     */
    loadPersistedState() {
      var key = this.storageKey();
      if (!key) return;

      try {
        var raw = window.sessionStorage.getItem(key);
        if (!raw) return;

        var saved = JSON.parse(raw);
        if (!saved || typeof saved !== 'object') return;

        this.state = Object.assign(defaultState(), saved, { step: 1 });
      } catch (error) {
        // sessionStorage unavailable (e.g. private browsing) or a corrupted
        // saved value - fall back to a blank draft rather than error out.
      }
    }

    persistState() {
      var key = this.storageKey();
      if (!key) return;

      try {
        window.sessionStorage.setItem(key, JSON.stringify(this.state));
      } catch (error) {
        // sessionStorage unavailable or full - the draft just won't persist.
      }
    }

    clearPersistedState() {
      var key = this.storageKey();
      if (!key) return;

      try {
        window.sessionStorage.removeItem(key);
      } catch (error) {
        // ignore
      }
    }

    /**
     * Liquid already dedupes placement labels when rendering the real Step 2
     * buttons/previews (first tagged image wins), but a merchant tagging two
     * images with the same placement name is worth a heads-up in the
     * console. Reads the JSON payload Liquid computed alongside that render
     * rather than re-deriving anything from alt text here.
     */
    logDuplicatePlacements() {
      var scriptEl = this.querySelector('[data-embroidery-media-json]');
      if (!scriptEl) return;

      try {
        var data = JSON.parse(scriptEl.textContent);
        (data.duplicateLabels || [])
          .filter(Boolean)
          .forEach(function (label) {
            console.warn(
              '[Embroidery] Duplicate placement label "' +
                label +
                '" on this product - using the first matching image, ignoring the rest.'
            );
          });
      } catch (error) {
        // malformed JSON - nothing to warn about
      }
    }

    disconnectedCallback() {
      if (this.boundHandleTriggerClick) {
        document.removeEventListener('click', this.boundHandleTriggerClick, true);
      }
      if (this.boundHandleFormSubmit) {
        document.removeEventListener('submit', this.boundHandleFormSubmit);
      }
      if (this.variantChangeUnsubscribe) {
        this.variantChangeUnsubscribe();
      }
    }

    /**
     * The main price display ("#Price-{section}") is a shared snippet
     * re-rendered wholesale (innerHTML swapped) on every variant change, so
     * a plain CSS/DOM insertion into it would get wiped out the next time
     * the customer picks a different size/color. Re-running this on every
     * `variant-change` pub/sub tick (Broadcast's own event, published from
     * product-info.js) keeps the "+ fee" addon attached without ever
     * touching that snippet's markup directly.
     */
    reattachPriceAddon() {
      if (!this.priceAddonEl || !this.sectionId) return;
      var priceEl = document.getElementById('Price-' + this.sectionId);
      if (!priceEl || priceEl.contains(this.priceAddonEl)) return;
      priceEl.appendChild(this.priceAddonEl);
    }

    updatePriceAddon() {
      if (!this.priceAddonEl) return;
      this.reattachPriceAddon();
      this.priceAddonEl.hidden = !this.state.completed;
    }

    /**
     * Some option sets (e.g. Shorts, which only embroiders at the hem) have
     * exactly one placement choice. Rather than hardcode which option sets
     * those are, pre-select it whenever there's only one placement button to
     * begin with - the step still shows so the customer sees where it goes,
     * but there's nothing to actually choose.
     */
    autoSelectSinglePlacement() {
      var placementButtons = this.optionButtons.filter(function (button) {
        return button.dataset.embroideryOption === 'placement';
      });
      if (placementButtons.length !== 1) return;

      var onlyOption = placementButtons[0];
      this.state.placement = onlyOption.dataset.value;
      this.state.placementMediaId = onlyOption.dataset.mediaId || '';
      onlyOption.setAttribute('aria-pressed', 'true');
    }

    bindEvents() {
      this.optionButtons.forEach(function (button) {
        button.addEventListener('click', this.handleOptionClick.bind(this, button));
      }.bind(this));

      if (this.textInput) {
        this.textInput.addEventListener('input', this.handleTextInput.bind(this));
        this.textInput.addEventListener('blur', this.handleTextBlur.bind(this));
      }

      this.patchInsertButtons.forEach(function (button) {
        button.addEventListener('click', this.insertPatch.bind(this, button.dataset.value));
      }.bind(this));

      if (this.backButton) {
        this.backButton.addEventListener('click', function () {
          this.goToStep(this.state.step - 1);
        }.bind(this));
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', function () {
          this.goToStep(this.state.step + 1);
        }.bind(this));
      }

      if (this.finishButton) {
        this.finishButton.addEventListener('click', this.handleFinish.bind(this));
      }

      if (this.removeButton) {
        this.removeButton.addEventListener('click', this.handleRemove.bind(this));
      }

      if (this.dialog) {
        this.dialog.addEventListener('close', this.handleDialogClosed.bind(this));
      }

      if (this.feeVariantId && this.productFormId) {
        this.boundHandleTriggerClick = this.handleTriggerClick.bind(this);
        document.addEventListener('click', this.boundHandleTriggerClick, true);

        this.boundHandleFormSubmit = this.handleFormSubmit.bind(this);
        document.addEventListener('submit', this.boundHandleFormSubmit);
      }

      if (this.priceAddonEl && typeof window.subscribe === 'function' && window.theme && window.theme.PUB_SUB_EVENTS) {
        this.variantChangeUnsubscribe = window.subscribe(
          window.theme.PUB_SUB_EVENTS.variantChange,
          this.reattachPriceAddon.bind(this)
        );
      }
    }

    handleOptionClick(button) {
      var group = button.dataset.embroideryOption;
      var value = button.dataset.value;

      if (group === 'threadColorLetter') {
        var letterIndex = parseInt(button.dataset.letterIndex, 10) - 1;
        this.state.threadColorLetters[letterIndex] = value;

        this.optionButtons
          .filter(function (candidate) {
            return (
              candidate.dataset.embroideryOption === 'threadColorLetter' &&
              candidate.dataset.letterIndex === button.dataset.letterIndex
            );
          })
          .forEach(function (candidate) {
            candidate.setAttribute('aria-pressed', String(candidate === button));
          });

        this.updateLiveText();
        this.updateNavState();
        this.persistState();
        return;
      }

      var typeChanged = group === 'type' && this.state.type !== value;

      this.state[group] = value;
      if (group === 'placement') this.state.placementMediaId = button.dataset.mediaId || '';

      this.optionButtons
        .filter(function (candidate) {
          return candidate.dataset.embroideryOption === group;
        })
        .forEach(function (candidate) {
          candidate.setAttribute('aria-pressed', String(candidate === button));
        });

      if (typeChanged) this.syncPersonalizationForType();
      if (group === 'placement') this.updatePreview();

      this.updateLiveText();
      this.updateNavState();
      this.persistState();
    }

    syncPersonalizationForType() {
      var isInitials = this.state.type !== 'Custom Name';
      var maxLength = isInitials ? this.initialsMaxLength : this.nameMaxLength;
      var heading = isInitials ? 'Monogram Initials' : 'Custom Name Embroidery';
      var label = isInitials ? 'Monogram Initials' : 'Custom Name';
      var placeholder = isInitials ? 'e.g. ABC' : 'e.g. Sydney';

      if (this.personalizeHeading) this.personalizeHeading.textContent = heading;
      if (this.textLabel) this.textLabel.textContent = label;

      if (this.textInput) {
        this.textInput.setAttribute('placeholder', placeholder);
        this.textInput.setAttribute('aria-label', label);

        var chars = Array.from(this.state.text);
        if (chars.length > maxLength) {
          this.state.text = chars.slice(0, maxLength).join('');
          this.textInput.value = this.state.text;
        }
      }

      if (this.initialsColorsEl) this.initialsColorsEl.hidden = !isInitials;
      if (this.nameColorsEl) this.nameColorsEl.hidden = isInitials;

      this.updateCharCount();
      this.updateLetterColorGroups();
    }

    updateCharCount() {
      if (!this.charCount) return;
      var maxLength = this.state.type === 'Custom Name' ? this.nameMaxLength : this.initialsMaxLength;
      this.charCount.textContent = Array.from(this.state.text).length + ' / ' + maxLength;
    }

    /**
     * Monogram Initials pre-renders one color-swatch group per letter
     * position up to the merchant's configured max length; only reveal as
     * many as the customer has actually typed, and label each with the
     * letter it's currently for.
     */
    updateLetterColorGroups() {
      var chars = Array.from(this.state.text);

      this.letterSwatchGroups.forEach(function (group) {
        var index = parseInt(group.dataset.embroideryLetterSwatches, 10);
        group.hidden = index > chars.length;
      });

      this.letterLabels.forEach(function (label) {
        var index = parseInt(label.dataset.embroideryLetterLabel, 10);
        var character = chars[index - 1];
        var patchName = character && PATCH_NAMES_BY_MARKER[character];

        if (patchName) {
          label.textContent = 'Letter ' + index + ' (' + patchName + ' patch)';
        } else if (character) {
          label.textContent = 'Letter ' + index + ' (' + character.toUpperCase() + ')';
        } else {
          label.textContent = 'Letter ' + index;
        }
      });
    }

    /**
     * Enforces the max length by Unicode code point rather than a native
     * `maxlength` attribute, since a patch marker can be a UTF-16 surrogate
     * pair - `maxlength` counts code units and would cut typing short (or
     * let it run long) once one is in the text. See insertPatch() for the
     * matching check on the patch-insert path.
     */
    handleTextInput() {
      var maxLength = this.state.type === 'Custom Name' ? this.nameMaxLength : this.initialsMaxLength;
      var chars = Array.from(this.textInput.value);

      if (chars.length > maxLength) {
        this.textInput.value = chars.slice(0, maxLength).join('');
      }

      this.state.text = this.textInput.value;
      this.updateCharCount();
      this.updateLetterColorGroups();
      this.updateLiveText();
      this.updateNavState();
      this.persistState();
    }

    handleTextBlur() {
      var trimmed = this.state.text.trim();
      if (trimmed === this.state.text) return;
      this.state.text = trimmed;
      this.textInput.value = trimmed;
      this.updateCharCount();
      this.updateLetterColorGroups();
      this.updateLiveText();
      this.updateNavState();
      this.persistState();
    }

    /**
     * Inserts a patch marker at the text input's current cursor position
     * (or replaces the current selection), exactly like typing a character
     * would - so a patch can end up first, last, or anywhere between
     * letters. Blocked, not truncated, if it would push the text past the
     * type's max length.
     */
    insertPatch(patchName) {
      var marker = PATCH_MARKERS[patchName];
      if (!marker || !this.textInput) return;

      var input = this.textInput;
      var start = input.selectionStart != null ? input.selectionStart : input.value.length;
      var end = input.selectionEnd != null ? input.selectionEnd : input.value.length;
      var maxLength = this.state.type === 'Custom Name' ? this.nameMaxLength : this.initialsMaxLength;
      var candidate = input.value.slice(0, start) + marker + input.value.slice(end);

      if (Array.from(candidate).length > maxLength) {
        this.announcePatchStatus('Text is full - remove a character to add a patch.');
        return;
      }

      input.value = candidate;
      this.state.text = candidate;

      this.updateCharCount();
      this.updateLetterColorGroups();
      this.updateLiveText();
      this.updateNavState();
      this.persistState();

      var newCursor = start + marker.length;
      input.focus();
      input.setSelectionRange(newCursor, newCursor);
      this.announcePatchStatus(patchName + ' patch added.');
    }

    announcePatchStatus(message) {
      if (this.patchStatusEl) this.patchStatusEl.textContent = message;
    }

    /**
     * The preview key is the tagged image's Shopify media id (set on both
     * the placement button and its matching preview `<div>` by Liquid),
     * not the placement label - labels are free-text from alt tags and
     * aren't safe or stable enough to use as a DOM/lookup key.
     */
    updatePreview() {
      var key = 'default';
      if (this.state.step !== 1 && this.state.placementMediaId) {
        key = this.state.placementMediaId;
      }

      this.previewImages.forEach(function (el) {
        el.classList.toggle('is-active', el.dataset.embroideryPreview === key);
      });

      if (this.mediaEl) {
        if (key === 'default') {
          delete this.mediaEl.dataset.activePlacement;
        } else {
          this.mediaEl.dataset.activePlacement = key;
        }
      }

      this.updateLiveText();
    }

    /**
     * Live-updates the customer's typed text directly on the chest/neck
     * photo - font follows the chosen style, position follows the
     * merchant's calibrated placement settings (CSS custom properties set
     * from Liquid on `.embroidery__media`), and color follows the chosen
     * thread swatch(es): Monogram Initials renders each position in its own
     * span so it can carry its own color, Custom Name shares one color
     * across every position since it's a single string. Iterates by Unicode
     * code point (Array.from), not String.prototype.length/.charAt, so a
     * patch marker - which can be a surrogate pair - is never split across
     * two positions. Any position whose character is a known patch marker
     * renders that patch's icon instead of the raw character, wherever it
     * falls in the string.
     */
    updateLiveText() {
      if (!this.liveTextEl) return;

      var chars = Array.from(this.state.text);
      var onPlacementPhoto = Boolean(this.mediaEl && this.mediaEl.dataset.activePlacement);
      var isInitials = this.state.type !== 'Custom Name';
      var nameColor = isInitials ? '' : this.getSwatchColor('threadColor', this.state.threadColor);

      this.liveTextEl.textContent = '';
      this.liveTextEl.classList.toggle('embroidery__live-text--hand-drawn', this.state.style === 'Hand-drawn');
      this.liveTextEl.classList.toggle('embroidery__live-text--script', this.state.style !== 'Hand-drawn');

      chars.forEach(function (character, i) {
        var color = isInitials
          ? this.getSwatchColor('threadColorLetter', this.state.threadColorLetters[i], i)
          : nameColor;

        var patchName = PATCH_NAMES_BY_MARKER[character];
        var node = patchName ? this.getPatchIconMarkup(patchName) : null;

        if (!node) {
          node = document.createElement('span');
          node.textContent = character;
        }

        if (color) node.style.color = color;
        this.liveTextEl.appendChild(node);
      }.bind(this));

      this.liveTextEl.hidden = !onPlacementPhoto || chars.length === 0;
    }

    /**
     * @param {string} group - "threadColor" (Custom Name) or
     *   "threadColorLetter" (one letter of Monogram Initials).
     * @param {string} value - the color name to look up (e.g. "Blue").
     * @param {number} [letterIndex] - required for "threadColorLetter".
     * @returns {HTMLElement|undefined} the matching swatch button, if any.
     */
    findSwatchButton(group, value, letterIndex) {
      if (!value) return undefined;

      return this.optionButtons.find(function (candidate) {
        if (candidate.dataset.embroideryOption !== group) return false;
        if (group === 'threadColorLetter') {
          return parseInt(candidate.dataset.letterIndex, 10) - 1 === letterIndex && candidate.dataset.value === value;
        }
        return candidate.dataset.value === value;
      });
    }

    /**
     * @returns {string} the swatch's hex color, or '' if none is set/found.
     */
    getSwatchColor(group, value, letterIndex) {
      var button = this.findSwatchButton(group, value, letterIndex);
      return button ? button.style.getPropertyValue('--embroidery-swatch-color') : '';
    }

    /**
     * The merchant-entered thread number for a given swatch - submitted as
     * a hidden ("_"-prefixed) line-item property, never shown to the
     * customer. See applyFieldsFromState().
     * @returns {string} the thread number, or '' if none is set/found.
     */
    getSwatchThreadNumber(group, value, letterIndex) {
      var button = this.findSwatchButton(group, value, letterIndex);
      return button ? button.dataset.threadNumber || '' : '';
    }

    /**
     * Clones the same icon already rendered for the given patch's "Add
     * Patch" button (Step 4) rather than duplicating its SVG markup here,
     * so the live preview glyph always matches exactly.
     */
    getPatchIconMarkup(patchName) {
      if (patchName === 'Zzz') {
        var text = document.createElement('span');
        text.className = 'embroidery__live-text__patch-text';
        text.setAttribute('aria-hidden', 'true');
        text.textContent = 'Zzz';
        return text;
      }

      var sourceButton = this.patchInsertButtons.find(function (button) {
        return button.dataset.value === patchName;
      });
      var svg = sourceButton && sourceButton.querySelector('svg');
      if (!svg) return null;

      var clone = svg.cloneNode(true);
      clone.setAttribute('class', 'embroidery__live-text__patch');
      clone.setAttribute('aria-hidden', 'true');
      return clone;
    }

    isStepValid(step) {
      switch (step) {
        case 1:
          return Boolean(this.state.type);
        case 2:
          return Boolean(this.state.placement);
        case 3:
          return Boolean(this.state.style);
        case 4:
          return this.isPersonalizationValid();
        default:
          return false;
      }
    }

    isPersonalizationValid() {
      var isInitials = this.state.type !== 'Custom Name';
      var maxLength = isInitials ? this.initialsMaxLength : this.nameMaxLength;
      var chars = Array.from(this.state.text.trim());

      if (chars.length === 0 || chars.length > maxLength) return false;

      if (isInitials) {
        for (var i = 0; i < chars.length; i++) {
          if (!this.state.threadColorLetters[i]) return false;
        }
        return true;
      }

      return Boolean(this.state.threadColor);
    }

    goToStep(step) {
      if (step < 1 || step > TOTAL_STEPS) return;
      if (step > this.state.step && !this.isStepValid(this.state.step)) return;

      this.state.step = step;
      this.renderStep();
      this.updatePreview();
      this.updateNavState();
      this.focusStepHeading();
    }

    focusStepHeading() {
      var section = this.stepSections[this.state.step - 1];
      var heading = section && section.querySelector('h3');
      if (!heading) return;
      heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: true });
    }

    renderStep() {
      this.stepSections.forEach(function (section) {
        section.hidden = parseInt(section.dataset.embroideryStep, 10) !== this.state.step;
      }.bind(this));

      if (this.stepIndicator) {
        this.stepIndicator.textContent = 'Step ' + this.state.step + ' of ' + TOTAL_STEPS;
      }

      this.syncOptionButtonsFromState();
    }

    syncOptionButtonsFromState() {
      this.optionButtons.forEach(function (button) {
        var group = button.dataset.embroideryOption;
        var pressed;

        if (group === 'threadColorLetter') {
          var letterIndex = parseInt(button.dataset.letterIndex, 10) - 1;
          pressed = this.state.threadColorLetters[letterIndex] === button.dataset.value;
        } else {
          pressed = this.state[group] === button.dataset.value;
        }

        button.setAttribute('aria-pressed', String(pressed));
      }.bind(this));

      if (this.textInput) this.textInput.value = this.state.text;
    }

    updateNavState() {
      if (this.backButton) this.backButton.hidden = this.state.step === 1;
      if (this.nextButton) this.nextButton.hidden = this.state.step === TOTAL_STEPS;
      if (this.finishButton) this.finishButton.hidden = this.state.step !== TOTAL_STEPS;

      if (this.nextButton) this.nextButton.disabled = !this.isStepValid(this.state.step);
      if (this.finishButton) this.finishButton.disabled = !this.isPersonalizationValid();
    }

    handleFinish() {
      if (!this.state.type || !this.state.placement || !this.state.style || !this.isPersonalizationValid()) {
        this.updateNavState();
        return;
      }

      this.state.text = this.state.text.trim();
      this.applyFieldsFromState();
      this.state.completed = true;

      this.renderSummary();
      this.updatePriceAddon();
      this.persistState();
      this.closeDialog();
    }

    /**
     * Writes the current state into the real form's hidden `properties[...]`
     * inputs. Split out from handleFinish() so a restored, already-completed
     * draft (see loadPersistedState()) can re-apply itself on page load
     * without duplicating this logic.
     */
    applyFieldsFromState() {
      var chars = Array.from(this.state.text);
      var isInitials = this.state.type !== 'Custom Name';

      this.setField('Embroidery', 'Yes');
      this.setField('Embroidery Type', this.state.type);
      this.setField('Embroidery Placement', this.state.placement);
      this.setField('Embroidery Style', this.state.style);
      this.setField('Embroidery Text', this.state.text);

      /**
       * Only one thread-color scheme is ever relevant at a time (the
       * whole-string color for Custom Name, or per-letter colors for
       * Monogram Initials) - explicitly disable whichever one ISN'T in use
       * so a stale value from an earlier Finish (a previous type, or a
       * longer set of initials since shortened) never rides along. Each
       * color's hidden thread-number property (see getSwatchThreadNumber())
       * follows the same one-in-use / rest-disabled pattern.
       */
      if (isInitials) {
        for (var i = 0; i < chars.length; i++) {
          var letterColor = this.state.threadColorLetters[i];
          this.setField('Embroidery Thread Color ' + (i + 1), letterColor);
          this.setField(
            '_Embroidery Thread Number ' + (i + 1),
            this.getSwatchThreadNumber('threadColorLetter', letterColor, i)
          );
        }
        for (var j = chars.length; j < this.initialsMaxLength; j++) {
          this.disableField('Embroidery Thread Color ' + (j + 1));
          this.disableField('_Embroidery Thread Number ' + (j + 1));
        }
        this.disableField('Embroidery Thread Color');
        this.disableField('_Embroidery Thread Number');
      } else {
        this.setField('Embroidery Thread Color', this.state.threadColor);
        this.setField('_Embroidery Thread Number', this.getSwatchThreadNumber('threadColor', this.state.threadColor));
        for (var k = 0; k < this.initialsMaxLength; k++) {
          this.disableField('Embroidery Thread Color ' + (k + 1));
          this.disableField('_Embroidery Thread Number ' + (k + 1));
        }
      }
    }

    setField(name, value) {
      var input = this.fields[name];
      if (!input) return;
      input.value = value;
      input.disabled = false;
    }

    disableField(name) {
      var input = this.fields[name];
      if (!input) return;
      input.value = '';
      input.disabled = true;
    }

    clearFields() {
      Object.keys(this.fields).forEach(function (name) {
        var input = this.fields[name];
        input.value = '';
        input.disabled = true;
      }.bind(this));
    }

    renderSummary() {
      var chars = Array.from(this.state.text);
      var parts = [this.state.type, this.state.placement, this.state.style, '“' + this.state.text + '”'];

      var patchNames = chars
        .map(function (character) {
          return PATCH_NAMES_BY_MARKER[character];
        })
        .filter(function (name, index, all) {
          return name && all.indexOf(name) === index;
        });
      if (patchNames.length) parts.push(patchNames.join('/') + ' patch');

      if (this.state.type === 'Custom Name') {
        if (this.state.threadColor) parts.push(this.state.threadColor);
      } else {
        var letterColors = this.state.threadColorLetters.slice(0, chars.length).filter(Boolean);
        if (letterColors.length) parts.push(letterColors.join('/'));
      }

      if (this.summaryTextEl) this.summaryTextEl.textContent = parts.join(' · ');
      if (this.summaryEl) this.summaryEl.hidden = false;
      if (this.triggerText) this.triggerText.textContent = this.editLabel;
    }

    handleRemove() {
      this.clearFields();
      this.state = defaultState();
      this.clearPersistedState();

      if (this.summaryEl) this.summaryEl.hidden = true;
      if (this.triggerText) this.triggerText.textContent = this.addLabel;
      if (this.textInput) this.textInput.value = '';

      this.autoSelectSinglePlacement();
      this.syncPersonalizationForType();

      this.renderStep();
      this.updatePreview();
      this.updateNavState();
      this.updatePriceAddon();
    }

    closeDialog() {
      if (this.popup && typeof this.popup.popupClose === 'function') {
        this.popup.popupClose();
      } else if (this.dialog && typeof this.dialog.close === 'function') {
        this.dialog.close();
      }
    }

    /**
     * The real interception point for adding the embroidery fee - hooks
     * "click", in the CAPTURE phase, on document, for the two buttons that
     * can actually add this product: the main form's real submit button,
     * and the sticky scroll cart bar's button (cart-bar.js).
     *
     * This used to only hook the form's "submit" event, on the assumption
     * every add-to-cart path was a real form submission. That's true for
     * the main button, but NOT for the cart bar: its button is
     * type="button", lives outside the form, and its click handler calls
     * the theme's ProductForm.onSubmitHandler(event) directly - a plain
     * method call, never a dispatched "submit" event - so a "submit"
     * listener never sees it and the fee silently never got added whenever
     * a customer used that bar. Capturing "click" on document fires before
     * either trigger's own handling (the theme's onSubmitHandler, or
     * cart-bar's handleClick) even starts, so both are blocked here, the
     * fee request is sent and awaited, and only then is the exact same
     * button re-clicked to let the theme's real add-to-cart logic run
     * untouched, exactly once - see replayClick().
     *
     * This also closes the race that caused the main button's fee to only
     * *sometimes* land: previously the fee's fetch() and the theme's own
     * add-to-cart fetch() both started at roughly the same time from two
     * separate paths and raced each other, so if the theme's request won
     * and navigated away first, ours (deliberately sent without
     * `keepalive` - see addEmbroideryFee()) could get cancelled mid-flight.
     * Now the fee request is fully awaited *before* the theme's own
     * request is even sent, so there's nothing left to race.
     *
     * `feeInFlight` swallows a second click landing in the brief window
     * before the fee request finishes (there's no visual feedback there
     * otherwise). `replayingButton` marks the one re-clicked button as
     * "already handled, let it through" so the replay doesn't loop back
     * into this same logic.
     */
    handleTriggerClick(event) {
      var formEl = this.productFormId ? document.getElementById(this.productFormId) : null;
      if (!formEl) return;

      var target = event.target.closest('button');
      if (!target || target === this.replayingButton) return;

      var isMainSubmit = target.type === 'submit' && target.form === formEl;
      var cartBarEl = !isMainSubmit && this.sectionId ? document.getElementById('CartBar-' + this.sectionId) : null;
      var isCartBarAdd = Boolean(
        cartBarEl && cartBarEl.contains(target) && target.hasAttribute('data-cart-bar-add-to-cart')
      );
      if (!isMainSubmit && !isCartBarAdd) return;

      if (this.feeInFlight) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      if (!this.state.completed) return;

      // Marks this draft as having actually been submitted, not just
      // finished in the modal - see verifyStillInCart(), which only runs
      // for a draft carrying this flag.
      this.state.addedToCart = true;
      this.persistState();

      var quantityInput = formEl.querySelector('input[name="quantity"]');
      var quantity = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;

      // Only the main button's replay results in a real "submit" event for
      // handleFormSubmit() to see - the cart bar's replay never does (see
      // its own comment above), so only arm that one-shot skip here.
      if (isMainSubmit) this.feeHandledForNextSubmit = true;

      this.feeInFlight = true;
      event.preventDefault();
      event.stopImmediatePropagation();
      target.disabled = true;

      this.addEmbroideryFee(quantity).then(
        function () {
          this.replayClick(target);
        }.bind(this),
        function (error) {
          // Don't let a fee-network hiccup block the customer's actual
          // add-to-cart - proceed without it rather than stall checkout.
          console.error('Embroidery fee could not be added to the cart:', error);
          this.replayClick(target);
        }.bind(this)
      );
    }

    replayClick(target) {
      this.feeInFlight = false;
      target.disabled = false;
      this.replayingButton = target;
      target.click();
      this.replayingButton = null;
    }

    /**
     * Backstop for the one add-to-cart path that never fires a "click" on
     * either trigger button - submitting the form via the Enter key.
     * Skipped entirely for the normal case (`feeHandledForNextSubmit`),
     * where handleTriggerClick() already added the fee and this event is
     * just the natural result of its own replayed click.
     */
    handleFormSubmit(event) {
      var formEl = this.productFormId ? document.getElementById(this.productFormId) : null;
      if (!formEl || event.target !== formEl) return;

      if (this.submittingFormEl === formEl) return;

      if (this.feeHandledForNextSubmit) {
        this.feeHandledForNextSubmit = false;
        return;
      }

      if (this.feeInFlight) {
        event.preventDefault();
        return;
      }

      if (!this.state.completed) return;

      this.state.addedToCart = true;
      this.persistState();

      var quantityInput = formEl.querySelector('input[name="quantity"]');
      var quantity = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;

      this.feeInFlight = true;
      event.preventDefault();

      this.addEmbroideryFee(quantity).then(
        function () {
          this.feeInFlight = false;
          this.replaySubmit(formEl);
        }.bind(this),
        function (error) {
          console.error('Embroidery fee could not be added to the cart:', error);
          this.feeInFlight = false;
          this.replaySubmit(formEl);
        }.bind(this)
      );
    }

    replaySubmit(formEl) {
      this.submittingFormEl = formEl;
      if (typeof formEl.requestSubmit === 'function') {
        formEl.requestSubmit();
      } else {
        formEl.submit();
      }
      this.submittingFormEl = null;
    }

    /**
     * A line-item property can't change what a product costs, so the fee is
     * a second, real, merchant-priced product added as its own cart line
     * right alongside the embroidered item - Shopify totals it into the
     * cart/checkout/order automatically, same as any other line item.
     *
     * Deliberately does NOT set `keepalive` - it looks like a reasonable
     * defense-in-depth for "the customer navigates away some other way
     * while this is in flight," but live testing against this store showed
     * it actually makes the request unreliable: it resolves with a normal
     * 200 and a correctly-shaped body, yet the item doesn't reliably show
     * up in the cart afterward, sometimes for seconds, sometimes not at
     * all. The exact same call without `keepalive` was immediate and
     * consistent every time. Since handleFormSubmit() already blocks
     * navigation until this promise settles, keepalive was never load-
     * bearing for the normal path anyway - it was only theoretical
     * protection for a rare edge case, and it was actively causing the real
     * failures being chased. Not worth the trade.
     *
     * Retries once, after a short pause, on any failure - including a
     * non-2xx response (a plain fetch() only rejects on a network-level
     * failure, never on an HTTP error status) or a response that isn't
     * actually JSON, which happens if a rate-limit/bot-check page comes
     * back instead of the real cart API response. Both showed up in real
     * testing against this store; a single retry is usually enough to get
     * past a transient one rather than silently dropping the fee.
     * @returns {Promise} resolves once the fee has actually been added.
     */
    addEmbroideryFee(quantity) {
      var body = JSON.stringify({
        items: [
          {
            id: this.feeVariantId,
            quantity: quantity,
            properties: { 'Added For': this.productTitle },
          },
        ],
      });

      function attempt() {
        return fetch(window.theme.routes.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: body,
        }).then(function (response) {
          var contentType = response.headers.get('content-type') || '';
          if (!response.ok || contentType.indexOf('json') === -1) {
            throw new Error('Embroidery fee request failed with status ' + response.status);
          }
          return response.json();
        });
      }

      return attempt().catch(function (error) {
        console.error('Embroidery fee request failed, retrying once:', error);
        return new Promise(function (resolve) {
          setTimeout(resolve, 600);
        }).then(attempt);
      });
    }

    /**
     * Draft selections are intentionally never cleared here: closing the
     * modal (Escape, backdrop click, the X button, or a successful Finish)
     * never discards what the customer picked. Only "Remove Embroidery"
     * resets state. Jumping back to step 1 on close keeps "the modal opens
     * on Step 1" true on every open while "don't reset on reopen" stays
     * true too, since all field values and pressed states persist.
     */
    handleDialogClosed() {
      this.state.step = 1;
      this.renderStep();
      this.updatePreview();
      this.updateNavState();
    }
  }

  customElements.define('embroidery-builder', EmbroideryBuilder);
})();